import React from "react";
import { Linkedin, Mail, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { LOGO_URL, COMPANY } from "@/lib/brand";

export const Footer = () => {
  const { t, lang, change, languages } = useLang();

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="main-footer"
      className="relative overflow-hidden gradient-footer text-white"
    >
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute -top-32 right-1/4 h-80 w-80 rounded-full bg-[#7C3AED] opacity-20 blur-[100px]" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-20">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="inline-flex h-14 items-center justify-center rounded-2xl bg-white px-3 shadow-elevated">
              <img src={LOGO_URL} alt="Genius WorkPA GmbH" className="h-11 w-auto object-contain" />
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-300">
              {t.footer.tagline}
            </p>
            <div className="mt-5 flex gap-2">
              <a
                href="#"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur transition-all hover:scale-110 hover:bg-white/10"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href={`mailto:${COMPANY.email}`}
                aria-label="Email"
                data-testid="footer-email"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 backdrop-blur transition-all hover:scale-110 hover:bg-white/10"
              >
                <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A5B4FC]">
              {t.footer.quick_links}
            </div>
            <ul className="mt-5 grid gap-2 text-sm text-slate-200">
              {["home", "about", "industries", "locations", "careers"].map((k) => (
                <li key={k}>
                  <button
                    onClick={() => go(k)}
                    data-testid={`footer-link-${k}`}
                    className="transition-colors hover:text-white"
                  >
                    {t.nav[k]}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A5B4FC]">
              {t.footer.services}
            </div>
            <ul className="mt-5 grid gap-2 text-sm text-slate-200">
              {t.services.items.slice(0, 5).map((s, i) => (
                <li key={i}>
                  <button onClick={() => go("services")} className="text-left transition-colors hover:text-white">
                    {s.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A5B4FC]">
              {t.footer.contact}
            </div>
            <div className="mt-5 space-y-3 text-sm text-slate-200">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-[#A78BFA]" />
                <div>
                  {COMPANY.street}<br />
                  {COMPANY.zip} {COMPANY.city}
                </div>
              </div>
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-2 transition-colors hover:text-white"
              >
                <Mail className="h-4 w-4 text-[#A78BFA]" />
                {COMPANY.email}
              </a>
            </div>

            {/* Language */}
            <div className="mt-7">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A5B4FC]">
                {t.footer.language}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => change(l.code)}
                    data-testid={`footer-lang-${l.code}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-all ${
                      lang === l.code
                        ? "border-white/40 bg-white/10 text-white"
                        : "border-white/10 text-slate-300 hover:border-white/20 hover:bg-white/5"
                    }`}
                  >
                    <span>{l.flag}</span>
                    <span className="uppercase">{l.code}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-7 text-xs text-slate-300 md:flex-row">
          <div>© {new Date().getFullYear()} {COMPANY.legal}. {t.footer.rights}</div>
          <div className="flex items-center gap-5">
            <a href="#" className="transition-colors hover:text-white">{t.footer.imprint}</a>
            <a href="#" className="transition-colors hover:text-white">{t.footer.privacy}</a>
            <a href="#" className="transition-colors hover:text-white">{t.footer.terms}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
